import type { FC } from "react";

import { SWReactIcons } from "@similarweb/icons";

import { WebsiteTooltip } from "app/components/React/Tooltip/WebsiteTooltip/WebsiteTooltip";
import { allTrackers } from "app/services/track/track";

import type { QualificationData, TrafficSourceRow } from "../../types";

import {
    Container,
    CellContentWrapper,
    TrafficSourceSpan,
    FaviconImage,
    InternalLink,
    ContentDiv,
    ChildrenCount,
    HoverIconsContainer,
    ExternalLink,
} from "./styledComponent";

const EXTERNAL_LINK_PREFIX = "http://";
const SOURCE_TYPES_WITH_ICONS = ["Referral", "Display Ad"] as const;

interface TrafficSourceCellProps {
    value: string;
    row: TrafficSourceRow;
    handleOpenQualificationSideBar?: (data: QualificationData) => void;
}

export const TrafficSourcesCell: FC<TrafficSourceCellProps> = ({ value, row, handleOpenQualificationSideBar }) => {
    const hasChildren = (row.children || []).length > 0;
    const childrenCount = row.children?.length || 0;
    const isChild = !!row.parent;
    const showMore =
        isChild && row.isLast && row.index !== undefined && row.parent?.childsCount !== undefined
            ? row.index + 1 !== row.parent.childsCount
            : false;
    const hasQualificationIcons = SOURCE_TYPES_WITH_ICONS.includes(row.sourceType as any);

    const handleExternalLinkClick = () => {
        allTrackers.trackEvent("External Link", "click", `Table/${value}`);
    };

    const handleWebsiteTooltipClick = () => {
        if (!handleOpenQualificationSideBar) {
            return;
        }

        handleOpenQualificationSideBar({
            Domain: value,
            Favicon: row.icon,
        });
    };

    if (!hasQualificationIcons) {
        return (
            <CellContentWrapper>
                <FaviconImage
                    src={row.icon}
                    alt=""
                />
                {value}
            </CellContentWrapper>
        );
    }

    return (
        <Container>
            <CellContentWrapper>
                <TrafficSourceSpan
                    $showMore={showMore}
                    $hasChildren={hasQualificationIcons}
                >
                    <FaviconImage
                        src={row.icon}
                        alt=""
                    />
                    <WebsiteTooltip
                        domain={value}
                        handleOpenQualificationSideBar={handleWebsiteTooltipClick}
                    >
                        <InternalLink href={row.url}>
                            <ContentDiv>{value}</ContentDiv>
                        </InternalLink>
                    </WebsiteTooltip>
                    {hasChildren && childrenCount > 0 ? <ChildrenCount>({childrenCount})</ChildrenCount> : null}
                </TrafficSourceSpan>
            </CellContentWrapper>

            <HoverIconsContainer>
                <ExternalLink
                    href={`${EXTERNAL_LINK_PREFIX}${value}`}
                    onClick={handleExternalLinkClick}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <SWReactIcons iconName="link-out" />
                </ExternalLink>
            </HoverIconsContainer>
        </Container>
    );
};

TrafficSourcesCell.displayName = "TrafficSourcesCell";
